import { useEffect, useState, type ReactNode } from "react";
import dayjs from "dayjs";
import clsx from "clsx";
import { db, eq } from "../lib/api";
import { invalidate, useQuery } from "../lib/hooks";
import { navigate } from "../lib/router";
import { AREA_LABEL, COUNT_LABEL, LEAD_AREAS, useRefData, type Area } from "../lib/refdata";
import { useSession } from "../lib/session";
import { PageHead } from "./Shell";
import { Empty, ErrorBox, Field, Loading, Panel, Sheet, useToast } from "./ui";
import { GxTargets } from "./Gx";

export type GoalRow = { id: string; title: string; why: string | null; count_key: string | null; period: "day" | "week"; target: number; actual: number;
  measure: string; period_from: string; period_to: string; expected: number };
type Goal = { id?: string; title: string; why: string | null; count_key: string | null; period: "day" | "week"; target: number; measure: string;
  activity_types: string[]; channels: string[]; areas: Area[]; applies_to: "everyone" | "role" | "user"; role_key: string | null; user_id: string | null;
  is_active: boolean; sort: number };

const COUNT_KEYS = ["outreach", "follow_ups", "appointments", "client_moves", "recruit_moves", "builder_moves"];
const MEASURE_LABEL: Record<string, string> = {
  activities: "Activities I log", new_contacts: "New contacts added", events: "Events booked or attended", posts: "Posts in the Content log", manual: "I tick it myself",
};

function Bar({ g }: { g: GoalRow }) {
  const pct = Math.min(100, (g.actual / g.target) * 100);
  const done = g.actual >= g.target;
  const behind = !done && g.period === "week" && g.actual < g.expected;
  return (
    <div className={clsx("goal", done && "done")}>
      <div className="goal-h">
        <span className="goal-t">{g.title}</span>
        <span className="goal-n"><b>{g.actual}</b> / {g.target}</span>
      </div>
      <div className="goal-bar"><i style={{ width: `${pct}%` }} className={behind ? "behind" : ""} /></div>
      {g.measure === "manual" && !done && <TickButtons g={g} />}
    </div>
  );
}
function TickButtons({ g }: { g: GoalRow }) {
  async function tick(d: number) { await db.rpc("goal_tick", { p_goal: g.id, p_delta: d }); invalidate("goals:"); }
  return <div className="goal-ticks"><button className="btn small" onClick={() => tick(1)}>+1 done</button>{g.actual > 0 && <button className="btn small ghost" onClick={() => tick(-1)}>Undo</button>}</div>;
}

/** Command Center: today's and this week's goals with progress. */
export function GoalsPanel({ extra }: { extra?: ReactNode }) {
  const { can } = useSession();
  const q = useQuery("goals:mine", () => db.rpc<GoalRow[]>("goal_progress"), { staleMs: 30_000 });
  const [tab, setTab] = useState<"day" | "week">("day");
  const [all, setAll] = useState(false);
  const rows = q.data ?? [];
  if (!rows.length && !extra) return null;
  const day = rows.filter((g) => g.period === "day"), week = rows.filter((g) => g.period === "week");
  const dayDone = day.filter((g) => g.actual >= g.target).length, weekDone = week.filter((g) => g.actual >= g.target).length;
  const list = tab === "day" ? day : week;
  return (
    <section className="panel cc-today">
      <div className="cc-today-h"><h2>Your goals</h2>
        {can("settings", "edit") && <button className="btn small ghost" onClick={() => navigate("/settings/goals")}>Edit</button>}</div>
      <div className="cc-tabs" role="tablist">
        <button role="tab" aria-selected={tab === "day"} className={clsx(tab === "day" && "on")} onClick={() => { setTab("day"); setAll(false); }}>Today {dayDone}/{day.length}</button>
        <button role="tab" aria-selected={tab === "week"} className={clsx(tab === "week" && "on")} onClick={() => { setTab("week"); setAll(false); }}>This week {weekDone}/{week.length}</button>
      </div>
      <div className="panel-body">
        {list.length ? (all ? list : list.slice(0, 5)).map((g) => <Bar key={g.id} g={g} />) : <div className="small muted">No {tab === "day" ? "daily" : "weekly"} goals set.</div>}
        {list.length > 5 && <button className="linkish small cc-morelink" onClick={() => setAll(!all)}>{all ? "Show fewer" : `Show all ${list.length} →`}</button>}
        <p className="small muted" style={{ margin: "6px 0 0" }}>They tick up as you log.</p>
      </div>
      {tab === "week" && extra}
    </section>
  );
}

/** Settings → Goals: write, share and switch off goals. */
export function SettingsGoals() {
  const { can } = useSession();
  const edit = can("settings", "edit");
  const q = useQuery("goals:all", () => db.select<Goal>("goals", { select: "*", order: "sort,title" }));
  const people = useQuery("goals:people", () => db.select<{ user_id: string; display_name: string }>("profiles", { select: "user_id,display_name", is_active: "is.true", order: "display_name" }));
  const roles = useQuery("goals:roles", () => db.select<{ key: string; label: string }>("roles", { select: "key,label", order: "sort" }));
  const [open, setOpen] = useState<Goal | null>(null);
  const who = (g: Goal) => g.applies_to === "everyone" ? "Everyone" : g.applies_to === "role"
    ? (roles.data?.find((r) => r.key === g.role_key)?.label ?? g.role_key) + (g.role_key === "owner" ? " (you)" : "")
    : people.data?.find((p) => p.user_id === g.user_id)?.display_name ?? "One person";
  const groups = [...COUNT_KEYS, null].map((k) => ({ k, rows: (q.data ?? []).filter((g) => g.count_key === k) })).filter((x) => x.rows.length);
  const blank: Goal = { title: "", why: null, count_key: "outreach", period: "day", target: 1, measure: "activities", activity_types: [], channels: [], areas: [],
    applies_to: "role", role_key: "owner", user_id: null, is_active: true, sort: 200 };
  return (
    <>
      <PageHead help="goals" title="Goals" sub="The specific habits that feed your six daily counts. They count themselves from what you log, and show on the Command Center, the weekly review and the 6 AM email."
        actions={edit && <button className="btn primary" onClick={() => setOpen(blank)}>New goal</button>} />
      <ErrorBox error={q.error} />
      <GxTargets />
      <h3 className="mt16" style={{ margin: "18px 0 0" }}>Daily and weekly habits</h3>
      {q.loading && !q.data ? <Loading /> : !groups.length ? <Panel><Empty title="No goals yet">Add the first habit you want to track.</Empty></Panel> : groups.map(({ k, rows }) => (
        <Panel key={k ?? "other"} title={k ? COUNT_LABEL[k] : "Other"} className="mt16">
          <div className="table-wrap"><table className="data">
            <thead><tr><th>Goal</th><th className="num">Target</th><th>Counted from</th><th>For</th><th>On</th></tr></thead>
            <tbody>{rows.map((g) => (
              <tr key={g.id} className={clsx(edit && "click", !g.is_active && "muted-row")} onClick={() => edit && setOpen(g)}>
                <td><b>{g.title}</b>{g.why && <div className="small muted">{g.why}</div>}</td>
                <td className="num nowrap">{g.target} / {g.period === "day" ? "day" : "week"}</td>
                <td className="small">{MEASURE_LABEL[g.measure]}{g.activity_types.length ? `: ${g.activity_types.join(", ")}` : ""}{g.channels.length ? ` · on ${g.channels.join(", ")}` : ""}
                  {g.measure === "new_contacts" ? `: ${(g.areas.length ? g.areas : ["new_connections", "hnw", "networking"] as Area[]).map((a) => AREA_LABEL[a]).join(", ")}` : ""}</td>
                <td className="small">{who(g)}</td>
                <td onClick={(e) => e.stopPropagation()}><input type="checkbox" disabled={!edit} checked={g.is_active} aria-label="Goal on"
                  onChange={async (e) => { await db.update("goals", { id: eq(g.id!) }, { is_active: e.target.checked }); invalidate("goals:"); }} /></td>
              </tr>
            ))}</tbody>
          </table></div>
        </Panel>
      ))}
      <p className="small muted">Starter goals were added for you (the owner) to edit. To share a goal, open it and change who it's for.</p>
      {open && <GoalSheet g={open} people={people.data ?? []} roles={roles.data ?? []} onClose={() => setOpen(null)} />}
    </>
  );
}

function GoalSheet({ g, people, roles, onClose }: { g: Goal; people: { user_id: string; display_name: string }[]; roles: { key: string; label: string }[]; onClose: () => void }) {
  const ref = useRefData();
  const toast = useToast();
  const [f, setF] = useState<Goal>(g);
  const [err, setErr] = useState<Error | null>(null);
  const set = <K extends keyof Goal>(k: K, v: Goal[K]) => setF((x) => ({ ...x, [k]: v }));
  const types = (ref.data?.types ?? []).filter((t: any) => !f.count_key || t.counts_as === f.count_key);
  useEffect(() => { if (f.measure !== "activities" && f.activity_types.length) set("activity_types", []); }, [f.measure]); // eslint-disable-line
  const toggle = <K extends "activity_types" | "channels" | "areas">(k: K, v: string) =>
    set(k, (f[k] as string[]).includes(v) ? (f[k] as string[]).filter((x) => x !== v) as Goal[K] : [...(f[k] as string[]), v] as Goal[K]);
  async function save() {
    setErr(null);
    try {
      if (!f.title.trim()) throw new Error("Give the goal a name");
      const body = { ...f, title: f.title.trim(), why: f.why || null, role_key: f.applies_to === "role" ? f.role_key : null, user_id: f.applies_to === "user" ? f.user_id : null };
      delete (body as any).id; delete (body as any).created_at; delete (body as any).updated_at; delete (body as any).created_by;
      if (g.id) await db.update("goals", { id: eq(g.id) }, body); else await db.insert("goals", body);
      invalidate("goals:"); toast("Goal saved"); onClose();
    } catch (e) { setErr(e as Error); }
  }
  async function remove() { await db.remove("goals", { id: eq(g.id!) }); invalidate("goals:"); toast("Goal deleted"); onClose(); }
  return (
    <Sheet title={g.id ? "Edit goal" : "New goal"} onClose={onClose} footer={<>
      {g.id && <button className="btn ghost danger" onClick={remove}>Delete</button>}
      <button className="btn primary" onClick={save}>Save</button></>}>
      <ErrorBox error={err} />
      <div className="form-grid">
        <Field label="Goal" wide><input className="input" value={f.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Reach out to new people on LinkedIn" /></Field>
        <Field label="Why it matters (optional)" wide><input className="input" value={f.why ?? ""} onChange={(e) => set("why", e.target.value)} /></Field>
        <Field label="How many"><input className="input" type="number" min={1} value={f.target} onChange={(e) => set("target", Math.max(1, +e.target.value || 1))} /></Field>
        <Field label="Every"><select className="select" value={f.period} onChange={(e) => set("period", e.target.value as Goal["period"])}><option value="day">Day</option><option value="week">Week</option></select></Field>
        <Field label="Feeds which count"><select className="select" value={f.count_key ?? ""} onChange={(e) => set("count_key", e.target.value || null)}>
          {COUNT_KEYS.map((k) => <option key={k} value={k}>{COUNT_LABEL[k]}</option>)}<option value="">Other</option></select></Field>
        <Field label="Counted from"><select className="select" value={f.measure} onChange={(e) => set("measure", e.target.value)}>
          {Object.entries(MEASURE_LABEL).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></Field>
        {f.measure === "activities" && <Field label={`Which activities (none ticked = every ${f.count_key ? COUNT_LABEL[f.count_key] : "counted"} activity)`} wide>
          <div className="chips">{types.map((t: any) => <button key={t.name} type="button" className={clsx("chip big", f.activity_types.includes(t.name) && "on")} onClick={() => toggle("activity_types", t.name)}>{t.name}</button>)}</div>
        </Field>}
        {(f.measure === "activities" || f.measure === "posts") && <Field label="Only on these channels (optional)" wide>
          <div className="chips">{(ref.data?.list("Channels") ?? []).map((c: string) => <button key={c} type="button" className={clsx("chip big", f.channels.includes(c) && "on")} onClick={() => toggle("channels", c)}>{c}</button>)}</div>
        </Field>}
        {f.measure === "new_contacts" && <Field label="Which lists count as new contacts" wide>
          <div className="chips">{LEAD_AREAS.filter((a) => a !== "warm").map((a) => <button key={a} type="button" className={clsx("chip big", f.areas.includes(a) && "on")} onClick={() => toggle("areas", a)}>{AREA_LABEL[a]}</button>)}</div>
          <span className="small muted">Warm market isn't counted: those are people you already know.</span>
        </Field>}
        <Field label="Who it's for"><select className="select" value={f.applies_to} onChange={(e) => set("applies_to", e.target.value as Goal["applies_to"])}>
          <option value="everyone">Everyone</option><option value="role">A role</option><option value="user">One person</option></select></Field>
        {f.applies_to === "role" && <Field label="Role"><select className="select" value={f.role_key ?? ""} onChange={(e) => set("role_key", e.target.value)}>
          {roles.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}</select></Field>}
        {f.applies_to === "user" && <Field label="Person"><select className="select" value={f.user_id ?? ""} onChange={(e) => set("user_id", e.target.value)}>
          <option value="">Choose…</option>{people.map((p) => <option key={p.user_id} value={p.user_id}>{p.display_name}</option>)}</select></Field>}
      </div>
    </Sheet>
  );
}

/** The 90-day plan: results you want by a date. Counted automatically (submitted and placed). */
type GoalPlan = { id?: string; name: string | null; start_date: string; end_date: string; targets: Record<string, number | null> };
const PLAN_LINES: { key: string; label: string; hint: string }[] = [
  { key: "clients", label: "New clients", hint: "Counted when the application is submitted, and again when placed" },
  { key: "applications", label: "Policies", hint: "Deals that reach Application submitted / Won" },
  { key: "premium", label: "Premium ($)", hint: "Life premium on the deal" },
  { key: "points", label: "Points", hint: "Points on the deal" },
  { key: "recruits", label: "Recruits", hint: "People who join your team" },
  { key: "licensed", label: "Recruits licensed", hint: "Licence stage set to Licensed" },
  { key: "events", label: "Events attended", hint: "Events marked Attended" },
];
// kept for the record; GX replaced it on the Goals page (v1.19.14)
export function PlanEditor() {
  const { profile } = useSession();
  const toast = useToast();
  const edit = profile?.role_key === "owner" || profile?.role_key === "admin";
  const plans = useQuery("goals:plans", () => db.select<GoalPlan>("goal_plans", { select: "*", order: "start_date.desc" }));
  const today = dayjs().format("YYYY-MM-DD");
  const curId = useQuery("goals:plans:current", () => db.rpc<string | null>("current_plan_id"));
  const current = (plans.data ?? []).find((p) => p.id === curId.data) ?? plans.data?.[0];
  const [f, setF] = useState<GoalPlan | null>(null);
  useEffect(() => { if (current && !f) setF(current); }, [current]); // eslint-disable-line react-hooks/exhaustive-deps
  const v = f ?? { name: "", start_date: today, end_date: dayjs().add(13, "week").subtract(1, "day").format("YYYY-MM-DD"), targets: {} };
  const weeks = Math.round((dayjs(v.end_date).diff(v.start_date, "day") + 1) / 7);
  async function save(asNew = false) {
    const body = { name: v.name || null, start_date: v.start_date, end_date: v.end_date,
      targets: Object.fromEntries(Object.entries(v.targets).filter(([, x]) => x != null && String(x) !== "" && Number(x) > 0).map(([k, x]) => [k, Number(x)])) };
    if (v.id && !asNew) await db.update("goal_plans", { id: eq(v.id) }, body);
    else { const r = await db.insert<GoalPlan>("goal_plans", body); setF(Array.isArray(r) ? r[0] : r); }
    invalidate("goals:"); invalidate("cycle:"); invalidate("cc"); toast("90-day plan saved: it's the one on the Command Center now");
  }
  async function remove() {
    if (!v.id) return;
    await db.remove("goal_plans", { id: eq(v.id) });
    setF(null); invalidate("goals:plans"); invalidate("cycle:"); invalidate("cc"); toast("Plan deleted");
  }
  function startNew() {
    const s = dayjs().format("YYYY-MM-DD");
    setF({ name: "", start_date: s, end_date: dayjs().add(13, "week").subtract(1, "day").format("YYYY-MM-DD"), targets: { ...(current?.targets ?? {}) } });
  }
  return (
    <Panel title="90-day plan: results" hint={v.id && v.id === current?.id ? "This plan is the one on the Command Center. Counted by itself from your deals, team and events." : v.id ? "Not on the Command Center right now. Press Save to make it the one shown." : "What you want to have achieved by the end date. Counted by itself from your deals, team and events."}
      actions={edit && <div style={{ display: "flex", gap: 6 }}>
        {v.id && (plans.data ?? []).length > 1 && <button className="btn small ghost" onClick={remove}>Delete this plan</button>}
        {v.id && <button className="btn small ghost" onClick={startNew}>Start a new plan</button>}
        <button className="btn small primary" onClick={() => save(!v.id)}>{v.id ? "Save" : "Create plan"}</button></div>}>
      <div className="panel-body" style={{ display: "grid", gap: 12 }}>
        <div className="plan-grid">
          <Field label="Name (optional)"><input className="input" disabled={!edit} value={v.name ?? ""} onChange={(e) => setF({ ...v, name: e.target.value })} placeholder="e.g. Q4 push" /></Field>
          <Field label="Starts"><input className="input" type="date" disabled={!edit} value={v.start_date} onChange={(e) => setF({ ...v, start_date: e.target.value })} /></Field>
          <Field label={`Ends (${weeks} weeks)`}><input className="input" type="date" disabled={!edit} value={v.end_date} min={v.start_date} onChange={(e) => setF({ ...v, end_date: e.target.value })} /></Field>
        </div>
        <div className="plan-grid">
          {PLAN_LINES.map((l) => (
            <Field key={l.key} label={l.label}>
              <input className="input" type="number" min={0} disabled={!edit} value={v.targets[l.key] ?? ""} placeholder="Off"
                onChange={(e) => setF({ ...v, targets: { ...v.targets, [l.key]: e.target.value === "" ? null : Number(e.target.value) } })} />
              <span className="small muted">{l.hint}</span>
            </Field>))}
        </div>
        {!edit && <p className="small muted" style={{ margin: 0 }}>Only the owner or an admin can change the plan.</p>}
        {(plans.data ?? []).length > 1 && <p className="small muted" style={{ margin: 0 }}>Other plans: {(plans.data ?? []).filter((p) => p.id !== v.id).map((p) =>
          <button key={p.id} className="linkish small" style={{ marginRight: 8 }} onClick={() => setF(p)}>{p.name || "Untitled"} ({dayjs(p.start_date).format("MMM D")}–{dayjs(p.end_date).format("MMM D")}){p.id === current?.id ? " · on the Command Center" : ""}</button>)}</p>}
      </div>
    </Panel>
  );
}
